import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Member } from "@chat-yai/shared";
import type { LeadStatus } from "@chat-yai/shared/admin-types";
import { leadsApi } from "@/api/endpoints/leads";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { permissionTooltip } from "@/lib/rbac";
import { EmptyState } from "@/components/EmptyState";
import { authStorage } from "@/lib/auth-storage";

const STATUSES: LeadStatus[] = ["new", "contacted", "qualified", "closed"];

interface Props {
  chatbotId: string;
  canEdit: boolean;
}

export function ChatbotLeadsTab({ chatbotId, canEdit }: Props): JSX.Element {
  return <LeadsTable chatbotId={chatbotId} canEdit={canEdit} embedded />;
}

interface LeadsTableProps {
  chatbotId?: string;
  canEdit: boolean;
  embedded?: boolean;
  // Permits the global LeadsListPage (if added) to disable the export button
  // for viewers without going through the role gate twice.
  canExport?: boolean;
}

export function LeadsTable({
  chatbotId,
  canEdit,
  embedded,
  canExport = true,
}: LeadsTableProps): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all">("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const membersQuery = useQuery({
    queryKey: ["tenants", "current", "members"],
    queryFn: () => tenantsApi.members(),
  });
  const members: Member[] = membersQuery.data?.members ?? [];

  const listQuery = useQuery({
    queryKey: ["leads", { chatbotId, page, statusFilter, assigneeFilter, search }],
    queryFn: () =>
      leadsApi.list({
        ...(chatbotId ? { chatbot_id: chatbotId } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(assigneeFilter !== "all"
          ? { assignee_user_id: assigneeFilter === "unassigned" ? null : assigneeFilter }
          : {}),
        ...(search ? { q: search } : {}),
        page,
        page_size: 20,
      }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) =>
      leadsApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.push("success", "Lead updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const updateAssignee = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      leadsApi.update(id, { assignee_user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.push("success", "Assignee updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const onExport = (): void => {
    const token = authStorage.get();
    const url = leadsApi.exportCsvUrl({
      ...(chatbotId ? { chatbot_id: chatbotId } : {}),
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      ...(assigneeFilter !== "all" && assigneeFilter !== "unassigned"
        ? { assignee_user_id: assigneeFilter }
        : {}),
      ...(search ? { q: search } : {}),
      ...(token ? { access_token: token } : {}),
    });
    // Trigger a download via a temporary anchor; browsers respect the
    // server's Content-Disposition: attachment header.
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.push("success", "Preparing CSV export.");
  };

  return (
    <div style={{ marginTop: embedded ? 16 : 0 }}>
      <div className="filters-bar" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="search-input">
          <Icon name="search" size={14} />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, email, phone"
            data-testid="leads-search"
          />
        </div>
        <select
          className="field-input"
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as LeadStatus | "all");
            setPage(1);
          }}
          data-testid="leads-status-filter"
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="field-input"
          style={{ width: 180 }}
          value={assigneeFilter}
          onChange={(e) => {
            setAssigneeFilter(e.target.value);
            setPage(1);
          }}
          data-testid="leads-assignee-filter"
        >
          <option value="all">All assignees</option>
          <option value="unassigned">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.email}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-outline btn-sm"
          onClick={onExport}
          disabled={!canExport || total === 0}
          title={!canExport ? permissionTooltip("non-support") : undefined}
          data-testid="leads-export-csv"
        >
          <Icon name="external" size={12} /> Export CSV
        </button>
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "1.4fr 1.6fr 1fr 1.6fr 1.4fr 130px 120px" }}
        >
          <div>Name</div>
          <div>Email</div>
          <div>Phone</div>
          <div>Summary</div>
          <div>Assignee</div>
          <div>Last seen</div>
          <div>Status</div>
        </div>
        {items.map((l) => (
          <div
            key={l.id}
            className="list-row"
            style={{
              gridTemplateColumns: "1.4fr 1.6fr 1fr 1.6fr 1.4fr 130px 120px",
            }}
            data-testid={`lead-row-${l.id}`}
          >
            <div style={{ fontSize: 13 }}>{l.name ?? "—"}</div>
            <div className="mono" style={{ fontSize: 12 }}>
              {l.email ?? "—"}
            </div>
            <div className="mono" style={{ fontSize: 12 }}>
              {l.phone ?? "—"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)" }}>
              {l.summary
                ? l.summary.slice(0, 70) + (l.summary.length > 70 ? "…" : "")
                : "—"}
            </div>
            <div>
              {canEdit ? (
                <select
                  className="field-input"
                  style={{ height: 28, padding: "2px 8px", fontSize: 12 }}
                  value={l.assignee_user_id ?? ""}
                  onChange={(e) =>
                    updateAssignee.mutate({
                      id: l.id,
                      userId: e.target.value === "" ? null : e.target.value,
                    })
                  }
                  data-testid={`lead-assignee-${l.id}`}
                >
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.email}
                    </option>
                  ))}
                </select>
              ) : (
                <span
                  style={{ fontSize: 12, color: "var(--text-3)" }}
                  title={permissionTooltip("write")}
                >
                  {l.assignee_email ?? "—"}
                </span>
              )}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(l.last_seen_at).toLocaleDateString()}
            </div>
            <div>
              {canEdit ? (
                <select
                  className="field-input"
                  style={{ height: 28, padding: "2px 8px", fontSize: 12 }}
                  value={l.status}
                  onChange={(e) =>
                    updateStatus.mutate({
                      id: l.id,
                      status: e.target.value as LeadStatus,
                    })
                  }
                  data-testid={`lead-status-${l.id}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : (
                <span
                  className={
                    "chip " +
                    (l.status === "new"
                      ? "chip-accent"
                      : l.status === "qualified"
                        ? "chip-success"
                        : l.status === "closed"
                          ? ""
                          : "chip-warning")
                  }
                  title={permissionTooltip("write")}
                >
                  {l.status}
                </span>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 ? (
          <EmptyState
            icon="mail"
            title={
              search || statusFilter !== "all" || assigneeFilter !== "all"
                ? "No leads match those filters"
                : "No leads yet"
            }
            description={
              search || statusFilter !== "all" || assigneeFilter !== "all"
                ? "Clear the search, status or assignee filter to see everything."
                : "Leads appear here once a visitor leaves contact details with the assistant. You can triage their status, reassign teammates and export to CSV."
            }
            testId="empty-leads"
          />
        ) : null}
        {total > 20 ? (
          <div className="pagination">
            <span>
              Page {page} of {totalPages} · {total} leads
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn btn-outline btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
